<script lang="ts">
	import { resolveAny } from '$lib/shared/lib';
	import Fa from 'svelte-fa';
	import { faArrowRight } from '@fortawesome/free-solid-svg-icons';
	import type { Snippet } from 'svelte';

	interface Props {
		href?: string;
		variant?: 'primary' | 'secondary' | 'gradient';
		size?: 'sm' | 'md' | 'lg' | 'xl';
		showArrow?: boolean;
		disabled?: boolean;
		onclick?: () => void;
		classes?: string;
		children?: Snippet;
	}

	let {
		href,
		variant = 'primary',
		size = 'md',
		showArrow = true,
		disabled = false,
		onclick,
		classes = '',
		children
	}: Props = $props();

	const variants = {
		primary: 'bg-primary-500 text-white',
		secondary: 'bg-secondary-500 text-white',
		gradient: 'bg-gradient-to-r from-primary-500 to-tertiary-500 text-white'
	};

	const sizes = {
		sm: 'px-4 py-1 text-sm',
		md: 'px-4 py-2 text-base',
		lg: 'px-6 py-3 text-lg',
		xl: 'px-8 py-4 text-xl'
	};

	// Shared button motion language: 200ms ease-out transitions, motion-safe lift/press
	// (see Button.svelte). Marketing CTAs are the pill, louder end of the same system.
	const baseClasses =
		'group inline-flex items-center justify-center gap-2 rounded-full font-semibold shadow-lg transition-transform duration-200 ease-out focus-visible:ring-2 focus-visible:ring-primary-500/60 focus-visible:ring-offset-2 focus-visible:ring-offset-surface-50 focus-visible:outline-none dark:focus-visible:ring-offset-surface-950';

	let stateClasses = $derived(
		disabled
			? 'cursor-not-allowed opacity-50'
			: 'motion-safe:hover:-translate-y-0.5 motion-safe:hover:scale-[1.03] motion-safe:active:scale-[0.98]'
	);
	let buttonClasses = $derived(
		`${baseClasses} ${sizes[size]} ${variants[variant]} ${stateClasses} ${classes}`
	);

	function handleClick(event: MouseEvent) {
		if (disabled) {
			event.preventDefault();
			return;
		}

		onclick?.();
	}
</script>

{#snippet content()}
	{@render children?.()}
	{#if showArrow}
		<span
			class="flex h-6 w-6 items-center justify-center rounded-full bg-surface-50 p-1 transition-transform group-hover:translate-x-0.5"
		>
			<Fa icon={faArrowRight} class="text-primary-500" size="1x" />
		</span>
	{/if}
{/snippet}

{#if href}
	<a
		href={disabled ? undefined : resolveAny(href)}
		class={buttonClasses}
		aria-disabled={disabled}
		tabindex={disabled ? -1 : undefined}
		onclick={handleClick}
	>
		{@render content()}
	</a>
{:else}
	<button type="button" class={buttonClasses} onclick={handleClick} {disabled}>
		{@render content()}
	</button>
{/if}
